/* import __COLOCATED_TEMPLATE__ from './pronunciation-side-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { modifier } from 'ember-modifier';
import type IntlService from 'embercom/services/intl';
import type AudioPreviewService from 'embercom/services/audio-preview';
import type { KeyboardType } from './ipa-keyboard-data';
import {
  FIN_VOICE_VOICE_LANGUAGE_OPTIONS,
  DEFAULT_VOICE_LANGUAGE,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import {
  DEFAULT_VOICE,
  getVoicesForLanguage,
  type FinVoiceVoice,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import { getLanguageFlag } from 'embercom/lib/language-flags';
import PronunciationTypeOption from './pronunciation-type-option';

export type TranslationType = 'phonetic' | 'ipa' | 'cmu';

export interface LanguageOverride {
  languageCode: string;
  languageName: string;
  pronunciation: string;
  translationType: TranslationType;
}

export interface Pronunciation {
  id: string;
  term: string;
  defaultPronunciation: string;
  defaultTranslationType: TranslationType;
  languageOverrides: LanguageOverride[];
}

export interface PronunciationFormData {
  term: string;
  defaultPronunciation: string;
  defaultTranslationType: TranslationType;
  languageOverrides: LanguageOverride[];
}

interface Signature {
  Args: {
    isOpen: boolean;
    pronunciation: Pronunciation | null;
    onSave: (data: PronunciationFormData) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
    onClose: () => void;
  };
  Element: HTMLElement;
}

export default class PronunciationSideDrawer extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare audioPreview: AudioPreviewService;

  @tracked isSaving = false;
  @tracked editTerm = '';
  @tracked editDefaultPronunciation = '';
  @tracked editDefaultTranslationType: TranslationType = 'phonetic';
  @tracked editLanguageOverridesEnabled = false;
  @tracked editLanguageOverrides: LanguageOverride[] = [];
  @tracked openOverrideSectionId: string | null = null;
  @tracked languageSearchQuery = '';
  @tracked showDeleteConfirmation = false;
  @tracked isDeleting = false;
  @tracked showSaveConfirmation = false;
  @tracked selectedDefaultVoice: FinVoiceVoice = DEFAULT_VOICE;
  @tracked selectedOverrideVoices: Map<string, FinVoiceVoice> = new Map();
  @tracked pronunciationInputElement: HTMLInputElement | null = null;

  captureInput = modifier((element: HTMLInputElement) => {
    this.pronunciationInputElement = element;
    return () => {
      this.pronunciationInputElement = null;
    };
  });

  private lastPronunciationId: string | null = null;
  private wasOpen = false;

  get isPlayingAudio(): boolean {
    return this.audioPreview?.isPlaying ?? false;
  }

  set isPlayingAudio(_value: boolean) {
    // No-op setter to prevent "has only a getter" error
  }

  get isCreatingNew(): boolean {
    return this.args.pronunciation === null;
  }

  get hasUnsavedChanges(): boolean {
    if (this.isCreatingNew) {
      return !!this.editTerm && !!this.editDefaultPronunciation;
    }

    let original = this.args.pronunciation;
    if (!original) {
      return false;
    }

    if (
      this.editTerm !== original.term ||
      this.editDefaultPronunciation !== original.defaultPronunciation ||
      this.editDefaultTranslationType !== original.defaultTranslationType
    ) {
      return true;
    }

    let currentOverrides = this.editLanguageOverridesEnabled ? this.editLanguageOverrides : [];
    let originalOverrides = original.languageOverrides ?? [];

    if (currentOverrides.length !== originalOverrides.length) {
      return true;
    }

    return currentOverrides.some((override, index) => {
      let orig = originalOverrides[index];
      return (
        override.languageCode !== orig.languageCode ||
        override.pronunciation !== orig.pronunciation ||
        override.translationType !== orig.translationType
      );
    });
  }

  get translationTypeOptions() {
    return [
      {
        value: 'phonetic',
        text: this.intl.t('ai-agent.voice-playground.pronunciation-manager.phonetic'),
        component: PronunciationTypeOption,
        componentAttrs: {
          text: this.intl.t('ai-agent.voice-playground.pronunciation-manager.phonetic'),
          description: this.intl.t('ai-agent.voice-playground.pronunciation-manager.recommended'),
        },
      },
      {
        value: 'ipa',
        text: this.intl.t('ai-agent.voice-playground.pronunciation-manager.ipa'),
        component: PronunciationTypeOption,
        componentAttrs: {
          text: this.intl.t('ai-agent.voice-playground.pronunciation-manager.ipa'),
        },
      },
      {
        value: 'cmu',
        text: this.intl.t('ai-agent.voice-playground.pronunciation-manager.cmu'),
        component: PronunciationTypeOption,
        componentAttrs: {
          text: this.intl.t('ai-agent.voice-playground.pronunciation-manager.cmu'),
        },
      },
    ];
  }

  get phoneticOnlyOptions() {
    return [
      {
        value: 'phonetic',
        text: this.intl.t('ai-agent.voice-playground.pronunciation-manager.phonetic'),
        component: PronunciationTypeOption,
        componentAttrs: {
          text: this.intl.t('ai-agent.voice-playground.pronunciation-manager.phonetic'),
          description: this.intl.t('ai-agent.voice-playground.pronunciation-manager.recommended'),
        },
      },
    ];
  }

  get availableLanguages() {
    return FIN_VOICE_VOICE_LANGUAGE_OPTIONS.filter((lang) => lang.value !== 'en-US').map(
      (lang) => ({
        value: lang.value,
        text: this.intl.t(lang.text),
      }),
    );
  }

  get showEnglishOnlyWarning(): boolean {
    return this.editDefaultTranslationType === 'ipa' || this.editDefaultTranslationType === 'cmu';
  }

  get unusedLanguages() {
    let unused = this.availableLanguages.filter(
      (lang) => !this.editLanguageOverrides.some((o) => o.languageCode === lang.value),
    );

    if (this.languageSearchQuery.trim()) {
      let query = this.languageSearchQuery.toLowerCase();
      unused = unused.filter((lang) => lang.text.toLowerCase().includes(query));
    }

    return unused;
  }

  getFlagForLanguage(languageCode: string): string {
    return getLanguageFlag(languageCode);
  }

  get availableDefaultVoices() {
    return getVoicesForLanguage(DEFAULT_VOICE_LANGUAGE).map((voice) => ({
      value: voice.value,
      text: voice.text,
    }));
  }

  get hasDefaultVoices(): boolean {
    return this.availableDefaultVoices?.length > 0;
  }

  getAvailableOverrideVoices(languageCode: string) {
    return getVoicesForLanguage(languageCode as any).map((voice) => ({
      value: voice.value,
      text: voice.text,
    }));
  }

  hasOverrideVoices = (languageCode: string): boolean => {
    let voices = this?.getAvailableOverrideVoices?.(languageCode) ?? [];
    return voices.length > 0;
  };

  getSelectedOverrideVoice(languageCode: string): FinVoiceVoice {
    return this.selectedOverrideVoices.get(languageCode) ?? DEFAULT_VOICE;
  }

  isDefaultVoiceSelected = (voiceItem: { value: FinVoiceVoice; text: string }): boolean => {
    return voiceItem.value === this.selectedDefaultVoice;
  };

  isOverrideVoiceSelected = (
    languageCode: string,
    voiceItem: { value: FinVoiceVoice; text: string },
  ): boolean => {
    let selectedVoice = this.getSelectedOverrideVoice(languageCode);
    return voiceItem.value === selectedVoice;
  };

  @action
  selectDefaultVoice(voiceItem: { value: FinVoiceVoice; text: string }) {
    this.selectedDefaultVoice = voiceItem.value;
  }

  @action
  selectOverrideVoice(languageCode: string, voiceItem: { value: FinVoiceVoice; text: string }) {
    this.selectedOverrideVoices.set(languageCode, voiceItem.value);
    this.selectedOverrideVoices = new Map(this.selectedOverrideVoices);
  }

  @action
  initializeForm() {
    let justOpened = this.args.isOpen && !this.wasOpen;
    this.wasOpen = this.args.isOpen;

    let currentId = this.args.pronunciation?.id ?? null;

    if (currentId !== this.lastPronunciationId || justOpened) {
      this.lastPronunciationId = currentId;

      if (this.args.pronunciation) {
        this.editTerm = this.args.pronunciation.term;
        this.editDefaultPronunciation = this.args.pronunciation.defaultPronunciation;
        this.editDefaultTranslationType = this.args.pronunciation.defaultTranslationType;
        this.editLanguageOverridesEnabled = this.args.pronunciation.languageOverrides.length > 0;
        this.editLanguageOverrides = [...this.args.pronunciation.languageOverrides];
      } else {
        this.editTerm = '';
        this.editDefaultPronunciation = '';
        this.editDefaultTranslationType = 'phonetic';
        this.editLanguageOverridesEnabled = false;
        this.editLanguageOverrides = [];
      }
      this.openOverrideSectionId = null;
      this.languageSearchQuery = '';
      this.selectedDefaultVoice = DEFAULT_VOICE;
      this.selectedOverrideVoices.clear();
    }
  }

  @action
  updateEditTerm(event: Event) {
    this.editTerm = (event.target as HTMLInputElement).value;
  }

  @action
  updateEditDefaultPronunciation(event: Event) {
    this.editDefaultPronunciation = (event.target as HTMLInputElement).value;
  }

  @action
  updateEditDefaultTranslationType(translationType: TranslationType) {
    this.editDefaultTranslationType = translationType;
  }

  get canShowKeyboard(): boolean {
    return this.editDefaultTranslationType === 'ipa' || this.editDefaultTranslationType === 'cmu';
  }

  get keyboardType(): KeyboardType {
    return this.editDefaultTranslationType as KeyboardType;
  }

  @action
  insertSymbolIntoPronunciation(symbol: string) {
    let input = this.pronunciationInputElement;
    if (input) {
      let start = input.selectionStart ?? this.editDefaultPronunciation.length;
      let end = input.selectionEnd ?? start;
      let before = this.editDefaultPronunciation.slice(0, start);
      let after = this.editDefaultPronunciation.slice(end);
      this.editDefaultPronunciation = before + symbol + after;

      let newCursor = start + symbol.length;
      requestAnimationFrame(() => {
        input?.focus();
        input?.setSelectionRange(newCursor, newCursor);
      });
    } else {
      this.editDefaultPronunciation += symbol;
    }
  }

  @action
  toggleLanguageOverrides() {
    this.editLanguageOverridesEnabled = !this.editLanguageOverridesEnabled;
    if (!this.editLanguageOverridesEnabled) {
      this.editLanguageOverrides = [];
    }
  }

  @action
  updateLanguageSearchQuery(event: Event) {
    this.languageSearchQuery = (event.target as HTMLInputElement).value;
  }

  @action
  clearLanguageSearch() {
    this.languageSearchQuery = '';
  }

  @action
  addLanguageOverride(languageCode: string) {
    let lang = this.availableLanguages.find((l) => l.value === languageCode);
    if (lang && !this.editLanguageOverrides.some((o) => o.languageCode === languageCode)) {
      this.editLanguageOverrides = [
        ...this.editLanguageOverrides,
        {
          languageCode: lang.value,
          languageName: lang.text,
          pronunciation: '',
          translationType: 'phonetic',
        },
      ];
      this.openOverrideSectionId = languageCode;
    }
    this.languageSearchQuery = '';
  }

  @action
  onOpenOverrideSectionChange(sectionId: string | null) {
    this.openOverrideSectionId = sectionId;
  }

  @action
  removeLanguageOverride(languageCode: string) {
    this.editLanguageOverrides = this.editLanguageOverrides.filter(
      (o) => o.languageCode !== languageCode,
    );
  }

  @action
  updateOverridePronunciation(languageCode: string, event: Event) {
    let target = event.target as HTMLInputElement;
    let value = target.value;
    let cursorPosition = target.selectionStart;

    this.editLanguageOverrides = this.editLanguageOverrides.map((o) =>
      o.languageCode === languageCode ? { ...o, pronunciation: value } : o,
    );

    // Use event.target directly instead of querying DOM
    requestAnimationFrame(() => {
      if (cursorPosition !== null && target) {
        target.setSelectionRange(cursorPosition, cursorPosition);
      }
    });
  }

  @action
  updateOverrideTranslationType(languageCode: string, translationType: TranslationType) {
    this.editLanguageOverrides = this.editLanguageOverrides.map((o) =>
      o.languageCode === languageCode ? { ...o, translationType } : o,
    );
  }

  @action
  async playOverridePronunciation(override: LanguageOverride, event?: Event) {
    event?.stopPropagation();
    let voice = this.getSelectedOverrideVoice(override.languageCode);
    await this.audioPreview.play(
      this.editTerm,
      override.pronunciation,
      override.translationType,
      voice,
      override.languageCode,
    );
  }

  @action
  async playEditPronunciation() {
    await this.audioPreview.play(
      this.editTerm,
      this.editDefaultPronunciation,
      this.editDefaultTranslationType,
      this.selectedDefaultVoice,
    );
  }

  @action
  async saveEditPanel() {
    this.isSaving = true;
    try {
      let formData: PronunciationFormData = {
        term: this.editTerm,
        defaultPronunciation: this.editDefaultPronunciation,
        defaultTranslationType: this.editDefaultTranslationType,
        languageOverrides: this.editLanguageOverridesEnabled ? this.editLanguageOverrides : [],
      };
      await this.args.onSave(formData);
    } finally {
      this.isSaving = false;
    }
  }

  @action
  showSaveConfirmationModal() {
    this.showSaveConfirmation = true;
  }

  @action
  cancelSave() {
    this.showSaveConfirmation = false;
  }

  @action
  async confirmSave() {
    this.showSaveConfirmation = false;
    await this.saveEditPanel();
  }

  @action
  showDeleteConfirmationModal() {
    this.showDeleteConfirmation = true;
  }

  @action
  cancelDelete() {
    this.showDeleteConfirmation = false;
  }

  @action
  async confirmDelete() {
    if (this.args.pronunciation) {
      this.isDeleting = true;
      try {
        await this.args.onDelete(this.args.pronunciation.id);
        this.showDeleteConfirmation = false;
      } finally {
        this.isDeleting = false;
      }
    }
  }

  @action
  closePanel() {
    this.showDeleteConfirmation = false;
    this.showSaveConfirmation = false;
    this.args.onClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::VoicePlayground::PronunciationSideDrawer': typeof PronunciationSideDrawer;
    'ai-agent/voice-playground/pronunciation-side-drawer': typeof PronunciationSideDrawer;
  }
}
